import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DatePickerErrorStateMatcher, endDateValidator } from '@common';
import { TranslateService } from '@ngx-translate/core';
import { GTMService } from '@services';
import { DevicesStore, InstallationFeaturesStore, isReady, Platform } from '@store';
import {
  Feature,
  FeatureNames,
  HolidayProgramCommands,
  HolidayProgramProperties,
  IsEnabledProperty,
} from '@vi-iot/client';
import moment from 'moment';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NoSameDateValidator } from 'src/app/shared/validators/no-same-date-range/no-same-date-validator';

import { MomentDateRangeForm } from '../../../../shared/common/form-types';

export interface HolidayModeData {
  startDate: Date;
  endDate: Date;
  circuitId: string;
}

@Component({
  selector: 'app-holiday-mode-datepicker',
  templateUrl: './holiday-mode-datepicker.component.html',
  styleUrls: ['./holiday-mode-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HolidayModeDatepickerComponent implements OnInit, OnDestroy {
  @Input() data: {
    platform: Platform;
    circuitId: string;
  };
  @Input() isHolidayAtHome: boolean;

  @Output() cancelSettingHolidayProgram: EventEmitter<void> = new EventEmitter<void>();
  @Output() submitHolidayProgram: EventEmitter<HolidayModeData> = new EventEmitter<
    HolidayModeData
  >();

  heatingCircuitTranslation = this.translateService.instant(
    '+INSTALLATIONS.INSTALLATION.CIRCUIT.CIRCUIT'
  );

  startDate: moment.Moment;
  endDate: moment.Moment;
  startDateFormControl: FormControl<moment.Moment>;
  endDateFormControl: FormControl<moment.Moment>;
  minStartDate: moment.Moment;
  minDaysDifference: number;
  selectedCircuit: string;
  circuits$: Observable<Feature<IsEnabledProperty, never>>;
  errorStateMatcher = new DatePickerErrorStateMatcher();
  dateRange: FormGroup<MomentDateRangeForm>;
  isMultiFamily: boolean;
  deviceTimeWithOffset: Date;

  private setDeviceTimeEnabled: boolean;

  private readonly DAYS_DIFFERENCE_LEGACY = 2;
  private readonly DAYS_DIFFERENCE_E3 = 0;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  get title(): string {
    return this.isHolidayAtHome
      ? '+INSTALLATIONS.INSTALLATION.HOLIDAY_AT_HOME.DIALOG.TITLE'
      : '+INSTALLATIONS.INSTALLATION.HOLIDAY_MODE.DIALOG.TITLE';
  }

  get description(): string {
    return this.isHolidayAtHome
      ? this.minDaysDifference
        ? '+INSTALLATIONS.INSTALLATION.HOLIDAY_AT_HOME.DIALOG.DESCRIPTION'
        : '+INSTALLATIONS.INSTALLATION.HOLIDAY_AT_HOME.DIALOG.DESCRIPTION_E3'
      : this.minDaysDifference
      ? '+INSTALLATIONS.INSTALLATION.HOLIDAY_MODE.DIALOG.DESCRIPTION'
      : '+INSTALLATIONS.INSTALLATION.HOLIDAY_MODE.DIALOG.DESCRIPTION_E3';
  }

  constructor(
    private translateService: TranslateService,
    private gtmService: GTMService,
    private devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.devicesStore.isHeatingDeviceTimeAvailable$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isAvailable: boolean) => {
        if (isAvailable) {
          this.setDeviceTimeEnabled = isAvailable;
        }
      });

    combineLatest([
      this.installationFeaturesStore.getOffsetDeviceTime(),
      this.installationFeaturesStore.selectInstallationDeviceFeature(
        this.isHolidayAtHome
          ? FeatureNames.circuitSingleFamilyHolidayAtHome
          : FeatureNames.circuitSingleFamilyHolidayProgram
      ),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        ([date, circuitSingleFamilyHolidayFeature]: [
          Date,
          Feature<HolidayProgramProperties, HolidayProgramCommands>
        ]) => {
          this.deviceTimeWithOffset = date;
          const sameDayAllowed =
            circuitSingleFamilyHolidayFeature?.commands?.changeEndDate?.params?.end?.constraints
              ?.sameDayAllowed === true;
          this.deviceTimeWithOffset = date;
          this.initForm(sameDayAllowed);
        }
      );

    this.devicesStore.isMultifamily$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isMulti) => {
      this.isMultiFamily = isMulti;
    });

    this.circuits$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuits
    );

    this.selectedCircuit = this.data?.circuitId;
    this.initHolidayFeatureData(this.data.circuitId);
  }

  onCircuitChange(circuit: string): void {
    this.selectedCircuit = circuit;
    this.initHolidayFeatureData(circuit);
    this.gtmService.track('onSelect', 'Heating circuit', 'holidayProgram');
  }

  onSubmit(): void {
    this.submitHolidayProgram.emit({
      startDate: this.startDateFormControl.value.toDate(),
      endDate: this.endDateFormControl.value.toDate(),
      circuitId: this.selectedCircuit,
    });
  }

  onCancelSelectingDate(): void {
    this.cancelSettingHolidayProgram.emit();
  }

  isFormValid(): boolean {
    return this.startDateFormControl.valid && this.endDateFormControl.valid;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private initForm(sameDayAllowed?: boolean): void {
    this.minDaysDifference =
      this.data.platform === Platform.E3 ? this.DAYS_DIFFERENCE_E3 : this.DAYS_DIFFERENCE_LEGACY;

    this.startDateFormControl = new FormControl<moment.Moment>(this.startDate, [
      Validators.required,
    ]);

    const endValidators = [
      Validators.required,
      endDateValidator(this.startDateFormControl, this.minDaysDifference),
    ];

    if (!sameDayAllowed) {
      endValidators.push(NoSameDateValidator(this.startDateFormControl));
    }
    this.endDateFormControl = new FormControl<moment.Moment>(this.endDate, endValidators);

    this.dateRange = new FormGroup<MomentDateRangeForm>({
      start: this.startDateFormControl,
      end: this.endDateFormControl,
    });

    this.minStartDate = this.getMinDate();

    this.startDateFormControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.endDateFormControl.updateValueAndValidity();
    });

    this.endDateFormControl.updateValueAndValidity();
  }

  private initHolidayFeatureData(circuitId: string): void {
    let holidayProgram$: Observable<Feature<HolidayProgramProperties, HolidayProgramCommands>>;

    if (this.isMultiFamily) {
      holidayProgram$ = this.isHolidayAtHome
        ? this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.circuitHolidayAtHome(circuitId)
          )
        : this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.circuitHolidayProgram(circuitId)
          );
    } else {
      holidayProgram$ = this.isHolidayAtHome
        ? this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.circuitSingleFamilyHolidayAtHome
          )
        : this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.circuitSingleFamilyHolidayProgram
          );
    }

    holidayProgram$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature))
      )
      .subscribe((holidayProgramFeature) => {
        this.startDate = this.getStartDate(holidayProgramFeature);
        this.endDate = this.getEndDate(holidayProgramFeature);
        this.startDateFormControl.setValue(this.startDate);
        this.endDateFormControl.setValue(this.endDate);
      });
  }

  private getStartDate(
    feature: Feature<HolidayProgramProperties, HolidayProgramCommands>
  ): moment.Moment {
    const start: string = feature?.properties?.start?.value;
    const isStartDateInPast: boolean = moment(start).isBefore(moment(), 'days');

    return start && !isStartDateInPast ? this.getNewMomentDate(start) : this.getNewMomentDate();
  }

  private getEndDate(
    feature: Feature<HolidayProgramProperties, HolidayProgramCommands>
  ): moment.Moment {
    const end: string = feature?.properties?.end?.value;
    const isEndDateInPast: boolean = moment(end).isBefore(moment(), 'days');

    return end && !isEndDateInPast
      ? this.getNewMomentDate(end)
      : moment().add(
          this.data.platform === Platform.E3
            ? this.DAYS_DIFFERENCE_E3
            : this.DAYS_DIFFERENCE_LEGACY,
          'd'
        );
  }

  private getMinDate(): moment.Moment {
    if (this.setDeviceTimeEnabled) {
      return this.getNewMomentDate(this.deviceTimeWithOffset);
    } else {
      return this.getNewMomentDate();
    }
  }

  private getNewMomentDate(value?: number | string | Date): moment.Moment {
    return value ? moment(new Date(value)).startOf('day') : moment(new Date()).startOf('day');
  }
}
